import React, { FormEvent, useState } from "react";
import {
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import styles from "./ContactUs.module.css";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

type SubmissionStatus =
  | { type: "idle"; message: "" }
  | { type: "success" | "error"; message: string };

const ContactUs: React.FC = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<SubmissionStatus>({
    type: "idle",
    message: "",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      setStatus({
        type: "error",
        message: "Please complete all fields before sending your message.",
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: "idle", message: "" });

    try {
      const response = await fetch(`${apiUrl}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          form_type: "contact",
          name: trimmedName,
          email: trimmedEmail,
          user_query: trimmedMessage,
        }),
      });

      const result = await response
        .json()
        .catch(() => ({} as { error?: string }));

      if (!response.ok) {
        throw new Error(
          result.error || "We couldn't send your message. Please try again."
        );
      }

      setName("");
      setEmail("");
      setMessage("");
      setStatus({
        type: "success",
        message: "Thanks for reaching out. We'll get back to you soon.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : "We couldn't send your message. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <SEO
        title="Contact Us | C-Shaped"
        description="Contact the C-Shaped support team for account, product, or technical help."
        canonical="https://c-shaped.com/contact-us"
      />
      <Header />

      <main className={styles.main}>
        <section className={styles.intro} aria-labelledby="contact-heading">
          <span className={styles.eyebrow}>We are here to help</span>
          <h1 id="contact-heading">Contact us</h1>
          <p className={styles.lead}>
            Have a question about C-Shaped, your account, or a technical issue?
            Send us a message and our team will follow up.
          </p>

          <div className={styles.contactOptions}>
            <a className={styles.contactOption} href="mailto:support@c-shaped.com">
              <span className={styles.iconWrapper} aria-hidden="true">
                <EnvelopeIcon />
              </span>
              <span>
                <strong>Email</strong>
                <small>support@c-shaped.com</small>
              </span>
            </a>

            <a
              className={styles.contactOption}
              href="https://wa.me/966558096476"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className={styles.iconWrapper} aria-hidden="true">
                <ChatBubbleLeftRightIcon />
              </span>
              <span>
                <strong>WhatsApp</strong>
                <small>Chat with our team</small>
              </span>
            </a>
          </div>
        </section>

        <section className={styles.formCard} aria-labelledby="message-heading">
          <div className={styles.formHeading}>
            <span className={styles.formIcon} aria-hidden="true">
              <PaperAirplaneIcon />
            </span>
            <div>
              <h2 id="message-heading">Send a message</h2>
              <p>All fields are required.</p>
            </div>
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.field}>
              <label htmlFor="contact-name">Name</label>
              <input
                id="contact-name"
                name="name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={120}
                required
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="contact-email">Email address</label>
              <input
                id="contact-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                maxLength={254}
                required
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="contact-message">How can we help?</label>
              <textarea
                id="contact-message"
                name="message"
                rows={7}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                maxLength={4000}
                required
              />
            </div>

            {status.type !== "idle" && (
              <div
                className={`${styles.status} ${
                  status.type === "success" ? styles.success : styles.error
                }`}
                role={status.type === "error" ? "alert" : "status"}
                aria-live="polite"
              >
                {status.message}
              </div>
            )}

            <button
              className={styles.submitButton}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send message"}
              {!isSubmitting && <PaperAirplaneIcon aria-hidden="true" />}
            </button>
          </form>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUs;
